import React, { useMemo, useState, useEffect } from 'react';

import { Row, Form, InputGroup } from 'react-bootstrap';
import { MdWarning } from 'react-icons/md';
import Select from 'react-select';

import { FileUploadButton } from '../../../../components';
import { validateExpenseDate, getDateInputConstraints, updateReceiptError } from '../../../../utils/dateValidation';
import { DisableClickOverlay, isBilledOrTransmitted } from '../../../../utils/utilities';
import DocumentRow from '../documentRow';
import { uploadExpenseFile } from '../operations';

import './styles.scoped.css';

const Receipt = ({
  receipt,
  id,
  isCR,
  receipts,
  receiptsErrors,
  customStyles,
  setUploading,
  setUploadError,
  form,
  handleDeleteRow,
  carRentalOptions,
  setReceiptFields,
  setReceiptField,
  debouncedSetReceiptField,
  setReceiptFile,
  deleteDocumentFromReceipt,
  expenseId,
  paymentOptions,
  setReceiptsErrors,
}) => {
  const [receiptAmountValue, setReceiptAmountValue] = useState(0);
  const [markupValue, setMarkupValue] = useState(0);
  const [dailyChargeValue, setDailyChargeValue] = useState(0);

  useEffect(() => {
    const receiptAmountToUse = receiptAmountValue || 0;
    const markupToUse = markupValue || 0;
    setReceiptField(id, 'expenseTotal', receiptAmountToUse * (1 + parseFloat(markupToUse) / 100), receipts);
  }, [receiptAmountValue, markupValue]);

  const handleDailyChargBlur = () => {
    if (dailyChargeValue === '') {
      setDailyChargeValue(0);
    }
  };

  useEffect(() => {
    setReceiptAmountValue(receipt.receiptAmount || 0);
  }, [receipt.receiptAmount]);

  useEffect(() => {
    setMarkupValue(receipt.markup || 0);
  }, [receipt.markup]);

  useEffect(() => {
    if (receipt.dailyCharge !== undefined && receipt.dailyCharge !== null) {
      setDailyChargeValue(receipt.dailyCharge);
    } else {
      setDailyChargeValue(0);
    }
  }, [receipt.dailyCharge]);

  const customCarRentalOptions = useMemo(() => {
    const result = carRentalOptions || [];
    if (
      receipt.expenseItemCarRentalCompanyId &&
      receipt.selectedCar &&
      !result.find(cro => cro.value === receipt.expenseItemCarRentalCompanyId)
    ) {
      result.push({
        label: receipt.selectedCar,
        value: receipt.expenseItemCarRentalCompanyId,
      });
      result?.sort((a, b) => (a.label > b.label ? 1 : -1));
    }
    return result;
  }, [carRentalOptions, receipt.expenseItemCarRentalCompanyId, receipt.selectedCar]);

  return (
    <div className="receipt-wrapper">
      {!receipt.documentName && (
        <div className="warning-container">
          <MdWarning size={36} title="Receipt document was not attached" color="#da0f2e" />
        </div>
      )}
      <DisableClickOverlay isDisabled={isBilledOrTransmitted(form) || isCR || form?.status === 'Voided'}>
        {receipts.length > 1 && (
          <div onClick={() => handleDeleteRow(id)} className="delete-row">
            Delete Row
          </div>
        )}

        <Row className="top-row" style={{ margin: '5px 0 0 0' }}>
          <div className="top-col" style={{ width: '40%', justifyContent: 'flex-start' }}>
            <div className="below-container">
              <Form.Group controlId="expenseItemCarRentalCompanyId">
                <Form.Label style={{ marginTop: '15px' }}>
                  <div className="required">Car Rental Company</div>
                </Form.Label>
                <div className="dropdown-spinner-container">
                  <Select
                    styles={customStyles('300px')}
                    options={customCarRentalOptions}
                    onChange={event => {
                      setReceiptFields(id, [
                        {
                          fieldName: 'expenseItemCarRentalCompanyId',
                          fieldValue: event.value,
                        },
                        {
                          fieldName: 'selectedCar',
                          fieldValue: event.label,
                        },
                      ]);
                    }}
                    value={customCarRentalOptions?.find(car => car.value === receipt.expenseItemCarRentalCompanyId)}
                  />
                </div>
                {receiptsErrors[id]?.expenseItemCarRentalCompanyId && (
                  <div className="error">{receiptsErrors[id]?.expenseItemCarRentalCompanyId}</div>
                )}
              </Form.Group>
            </div>
          </div>
          <div className="top-col">
            <Form.Group controlId="carRentalContractNumber">
              <Form.Label style={{ marginTop: '15px' }}>
                <div className="required"> Rental Contract Number</div>
              </Form.Label>
              <Form.Control
                key={`${id}carRentalContractNumber`}
                style={{
                  width: '300px',
                  height: '30px',
                  paddingLeft: 10,
                }}
                type="input"
                name="carRentalContractNumber"
                defaultValue={receipt?.carRentalContractNumber}
                onBlur={event => {
                  setReceiptField(id, 'carRentalContractNumber', event.target.value, receipts);
                }}
                isInvalid={!!receiptsErrors[id]?.carRentalContractNumber}
              />
              <Form.Control.Feedback type="invalid">
                {receiptsErrors[id]?.carRentalContractNumber}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
        </Row>
        <Row className="top-row" style={{ margin: '5px 0 0 0' }}>
          <div className="top-col" style={{ width: '40%', justifyContent: 'flex-start' }}>
            <div className="below-container">
              <Form.Group controlId="carRentalPickUpLocation">
                <Form.Label style={{ marginTop: '15px' }}>
                  <div className="required"> Pick Up Location</div>
                </Form.Label>
                <Form.Control
                  key={`${id}carRentalPickUpLocation`}
                  style={{
                    width: '300px',
                    height: '30px',
                    paddingLeft: 10,
                  }}
                  type="input"
                  name="carRentalPickUpLocation"
                  defaultValue={receipt?.carRentalPickUpLocation}
                  onChange={event => {
                    debouncedSetReceiptField({
                      id: id,
                      field: 'carRentalPickUpLocation',
                      value: event.target.value,
                      receipts: receipts,
                    });
                  }}
                  isInvalid={!!receiptsErrors[id]?.carRentalPickUpLocation}
                />
                <Form.Control.Feedback type="invalid">
                  {receiptsErrors[id]?.carRentalPickUpLocation}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>
          <div className="top-col">
            <Form.Group controlId="carRentalReturnLocation">
              <Form.Label style={{ marginTop: '15px' }}>
                <div className="required"> Return Location</div>
              </Form.Label>
              <Form.Control
                key={`${id}carRentalReturnLocation`}
                style={{
                  width: '300px',
                  height: '30px',
                  paddingLeft: 10,
                }}
                type="input"
                name="carRentalReturnLocation"
                defaultValue={receipt?.carRentalReturnLocation}
                onChange={event => {
                  debouncedSetReceiptField({
                    id: id,
                    field: 'carRentalReturnLocation',
                    value: event.target.value,
                    receipts: receipts,
                  });
                }}
                isInvalid={!!receiptsErrors[id]?.carRentalReturnLocation}
              />
              <Form.Control.Feedback type="invalid">
                {receiptsErrors[id]?.carRentalReturnLocation}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
        </Row>

        <Row className="top-row" style={{ margin: '0' }}>
          <div className="top-col" style={{ width: '40%', justifyContent: 'flex-start' }}>
            <div className="below-container">
              <div className="below-container" style={{ justifyContent: 'flex-start', width: '300px' }}>
                <Form.Group controlId="carRentalPickUpDate">
                  <Form.Label style={{ marginTop: '15px' }}>
                    <div className="required">Pick Up Date</div>
                  </Form.Label>
                  <Form.Control
                    style={{ width: '150px', height: '30px' }}
                    name="carRentalPickUpDate"
                    type="date"
                    as="input"
                    defaultValue={receipt?.carRentalPickUpDate}
                    min={getDateInputConstraints().min}
                    max={getDateInputConstraints().max}
                    onChange={event => {
                      const validation = validateExpenseDate(event.target.value, 'Pick Up Date');
                      updateReceiptError(
                        id,
                        'carRentalPickUpDate',
                        validation.isValid ? null : validation.error,
                        receiptsErrors,
                        setReceiptsErrors,
                      );
                      setReceiptField(id, 'carRentalPickUpDate', event.target.value, receipts);
                    }}
                    isInvalid={!!receiptsErrors[id]?.carRentalPickUpDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {receiptsErrors[id]?.carRentalPickUpDate}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>
          </div>
          <div className="top-col" style={{ justifyContent: 'flex-start' }}>
            <div className="below-container" style={{ justifyContent: 'flex-start', width: '300px' }}>
              <Form.Group controlId="carRentalReturnDate">
                <Form.Label style={{ marginTop: '15px' }}>
                  <div className="required">Return Date</div>
                </Form.Label>
                <Form.Control
                  style={{ width: '150px', height: '30px' }}
                  name="carRentalReturnDate"
                  type="date"
                  as="input"
                  defaultValue={receipt?.carRentalReturnDate}
                  min={getDateInputConstraints().min}
                  max={getDateInputConstraints().max}
                  onChange={event => {
                    const validation = validateExpenseDate(event.target.value, 'Return Date');
                    updateReceiptError(
                      id,
                      'carRentalReturnDate',
                      validation.isValid ? null : validation.error,
                      receiptsErrors,
                      setReceiptsErrors,
                    );
                    setReceiptField(id, 'carRentalReturnDate', event.target.value, receipts);
                  }}
                  isInvalid={!!receiptsErrors[id]?.carRentalReturnDate}
                />
                <Form.Control.Feedback type="invalid">{receiptsErrors[id]?.carRentalReturnDate}</Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>
          <div className="top-col">
            <Form.Group controlId="projectedDays">
              <Form.Label style={{ marginTop: '15px' }}>Projected Days</Form.Label>
              <Form.Control
                style={{
                  width: '225px',
                  height: '30px',
                  padding: 0,
                  border: '0px',
                  backgroundColor: 'white',
                  color: 'gray',
                  paddingLeft: '5px',
                }}
                as="input"
                name="projectedDays"
                readOnly
                disabled
                value={receipt?.projectedDays ? receipt?.projectedDays : '0'}
              />
            </Form.Group>
          </div>
        </Row>
        <Row className="top-row" style={{ margin: '5px 0 0 0' }}>
          <div className="top-col" style={{ width: '40%', justifyContent: 'flex-start' }} />
          <div className="top-col" style={{ justifyContent: 'flex-start' }}>
            <div className="below-container" style={{ justifyContent: 'flex-start', width: '300px' }}>
              <Form.Group controlId="dailyCharge">
                <Form.Label style={{ marginTop: '15px' }}>
                  <div className="required">Daily Charge</div>
                </Form.Label>
                <InputGroup
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '150px',
                  }}>
                  <span
                    style={{
                      position: 'absolute',
                      left: '10px',
                      zIndex: 10000,
                    }}>
                    $
                  </span>
                  <Form.Control
                    key={`${id}dailyCharge`}
                    style={{
                      width: '150px',
                      height: '30px',
                      padding: 0,
                      paddingLeft: '25px',
                    }}
                    type="number"
                    step="0.01"
                    min="0"
                    name="dailyCharge"
                    value={dailyChargeValue}
                    onChange={event => {
                      setDailyChargeValue(event.target.value);
                      debouncedSetReceiptField({
                        id: id,
                        field: 'dailyCharge',
                        value: event.target.value,
                        receipts: receipts,
                      });
                    }}
                    onBlur={handleDailyChargBlur}
                    isInvalid={!!receiptsErrors[id]?.dailyCharge}
                  />
                </InputGroup>
                <Form.Control.Feedback type="invalid">{receiptsErrors[id]?.dailyCharge}</Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>
          <div className="top-col">
            <Form.Group controlId="expenseTotal">
              <Form.Label style={{ marginTop: '15px' }}>Projected Amount</Form.Label>
              <InputGroup
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '150px',
                  opacity: '0.7',
                }}>
                <span
                  style={{
                    position: 'absolute',
                    left: '10px',
                    zIndex: 10000,
                  }}>
                  $
                </span>
                <Form.Control
                  style={{
                    width: '225px',
                    height: '30px',
                    padding: 0,
                    border: '0px',
                    backgroundColor: 'white',
                    marginLeft: '20px',
                  }}
                  as="input"
                  name="location"
                  readOnly
                  disabled
                  value={
                    receipt?.projectedAmount
                      ? parseFloat(receipt?.projectedAmount).toFixed(2)
                      : parseFloat(0).toFixed(2)
                  }
                />
              </InputGroup>
            </Form.Group>
          </div>
        </Row>
        {form?.lob === 'DMS' && (
          <>
            <Row className="top-row" style={{ margin: '5px 0 0 0' }}>
              <div className="top-col" style={{ width: '40%', justifyContent: 'flex-start' }} />
              <div className="top-col" style={{ justifyContent: 'flex-start' }}>
                <div className="below-container" style={{ justifyContent: 'flex-start', width: '300px' }}>
                  <Form.Group controlId="paidThrough">
                    <Form.Label style={{ marginTop: '15px' }}>
                      <div className="required">Paid Through</div>
                    </Form.Label>
                    <Form.Control
                      style={{ width: '225px', height: '30px', padding: 0 }}
                      as="select"
                      name="paidThrough"
                      value={receipt.paidThrough}
                      onChange={event => {
                        setReceiptField(id, 'paidThrough', event.target.value, receipts);
                      }}
                      onBlur={() => {
                        if (receipt.paidThrough !== 'Credit Card') {
                          setReceiptField(id, 'creditCardHolderName', '', receipts);
                        }
                      }}
                      isInvalid={!!receiptsErrors[id]?.paidThrough}
                      defaultValue="">
                      <option value="" disabled>
                        Select
                      </option>
                      {paymentOptions}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">{receiptsErrors[id]?.paidThrough}</Form.Control.Feedback>
                  </Form.Group>
                </div>
                <Form.Control.Feedback type="invalid">{receiptsErrors[id]?.paidThrough}</Form.Control.Feedback>
              </div>
              <div className="top-col">
                <Form.Group controlId="creditCardHolderName">
                  <InputGroup
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '150px',
                    }}>
                    <Form.Group controlId="creditCardHolderName">
                      <Form.Label
                        style={{
                          marginTop: '15px',
                        }}>
                        <div className="required">Credit Card Holder Name</div>
                      </Form.Label>
                      <Form.Control
                        disabled={receipt?.paidThrough !== 'Credit Card'}
                        name="creditCardHolderName"
                        style={{
                          width: '225px',
                          height: '30px',
                          paddingLeft: 10,
                        }}
                        onChange={event => {
                          setReceiptField(id, 'creditCardHolderName', event.target.value, receipts);
                        }}
                        type="input"
                        value={receipt?.paidThrough !== 'Credit Card' ? '' : receipt?.creditCardHolderName}
                        defaultValue=""
                        isInvalid={
                          receipt?.paidThrough !== 'Credit Card' ? false : !!receiptsErrors[id]?.creditCardHolderName
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {receipt?.paidThrough !== 'Credit Card' ? false : receiptsErrors[id]?.creditCardHolderName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </InputGroup>
                </Form.Group>
              </div>
            </Row>
          </>
        )}
        <Row className="top-row" style={{ margin: '5px 0 0 0', alignItems: 'start' }}>
          <div className="top-col" style={{ width: '40%', justifyContent: 'flex-start' }}>
            <div className="below-container" style={{ justifyContent: 'flex-end' }}>
              <div
                className="below-container"
                style={{
                  justifyContent: 'space-between',
                  width: '300px',
                }}>
                <Form.Group controlId="receiptAmount">
                  <Form.Label style={{ marginTop: '15px' }}>
                    <div>Receipt Amount</div>
                  </Form.Label>
                  <InputGroup style={{ display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        position: 'absolute',
                        left: '10px',
                        zIndex: 10000,
                      }}>
                      $
                    </span>
                    <Form.Control
                      key={`${id}receiptAmount`}
                      style={{
                        width: '150px',
                        height: '30px',
                        padding: 0,
                        paddingLeft: '25px',
                      }}
                      type="number"
                      step="0.01"
                      min="0"
                      name="receiptAmount"
                      value={receiptAmountValue}
                      onChange={event => {
                        setReceiptAmountValue(event.target.value);
                        setReceiptField(id, 'receiptAmount', Number(event.target.value), receipts);
                      }}
                      isInvalid={!!receiptsErrors[id]?.receiptAmount}
                    />
                  </InputGroup>
                </Form.Group>
                <Form.Group controlId="markup">
                  <Form.Label style={{ marginTop: '15px' }}>
                    <div className="required">Markup</div>
                  </Form.Label>
                  <InputGroup style={{ display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        position: 'absolute',
                        right: '10px',
                        zIndex: 10000,
                      }}>
                      %
                    </span>
                    <Form.Control
                      style={{
                        width: '75px',
                        height: '30px',
                        padding: 0,
                        paddingLeft: '10px',
                      }}
                      type="number"
                      step="1"
                      min="0"
                      name="markup"
                      value={markupValue}
                      onChange={event => {
                        setMarkupValue(event.target.value);
                        setReceiptField(id, 'markup', event.target.value, receipts);
                      }}
                      isInvalid={!!receiptsErrors[id]?.markup}
                    />
                  </InputGroup>

                  <Form.Control.Feedback type="invalid">{receiptsErrors[id]?.markup}</Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>
          </div>
          <div className="top-col">
            <Form.Group controlId="expenseTotal">
              <Form.Label style={{ marginTop: '15px' }}>Expense Total</Form.Label>
              <InputGroup
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '150px',
                  opacity: '0.7',
                }}>
                <span
                  style={{
                    position: 'absolute',
                    left: '10px',
                    zIndex: 10000,
                  }}>
                  $
                </span>
                <Form.Control
                  style={{
                    width: '225px',
                    height: '30px',
                    padding: 0,
                    border: '0px',
                    backgroundColor: 'white',
                    marginLeft: '20px',
                  }}
                  as="input"
                  name="location"
                  readOnly
                  disabled
                  value={
                    receipt?.expenseTotal ? parseFloat(receipt?.expenseTotal).toFixed(2) : parseFloat(0).toFixed(2)
                  }
                />
              </InputGroup>
            </Form.Group>
          </div>
          {!receipt?.documentName && expenseId ? (
            <div className="top-col">
              <Form.Group controlId="receipt" style={{ display: 'flex', flexDirection: 'column' }}>
                <Form.Label style={{ marginTop: '15px' }}>
                  <div>Receipt</div>
                </Form.Label>

                <Form.Control
                  name="receipt"
                  as="span"
                  style={{
                    border: 'none',
                    outline: 'none',
                    paddingLeft: 0,
                    height: 'auto',
                  }}>
                  <FileUploadButton
                    func={uploadExpenseFile}
                    setReceiptFile={setReceiptFile}
                    receiptId={id}
                    uploadStart={() => setUploading(true)}
                    uploadEnd={() => setUploading(false)}
                    uploadError={() => {
                      setUploading(false);
                      setUploadError('Upload failed. Please ensure you have a network connection and try again.');
                    }}
                    expenseId={expenseId}
                  />
                </Form.Control>
              </Form.Group>
            </div>
          ) : null}
        </Row>
      </DisableClickOverlay>
      {receipt?.documentName && (
        <DocumentRow
          id={id}
          deleteDocumentFromReceipt={deleteDocumentFromReceipt}
          receipt={receipt}
          disabled={isBilledOrTransmitted(form) || form.status === 'Voided' || isCR}
        />
      )}
      <div className="gray-divider-light" />
    </div>
  );
};

export default Receipt;
